/**
 * CaseStudyCard.jsx — Public-Site Variante
 * ==============================================================================
 *
 * Rendert die "Case-Study"-Card für die smartRATINGS-Public-Site.
 * Konsumiert das Datenpaket vom Public-Endpoint
 *   GET /api/admin/case-study/public/:customerId/card-data
 *
 * Unterschiede zur Admin-Variante (admin-dashboard/components/case-study/CaseStudyCard.jsx):
 *   - Verwendet KEIN recharts (custom SVG-Chart, weil Homepage-Bundle clean bleiben soll)
 *   - Verwendet KEIN tailwind (eigene CSS-Klassen via CaseStudyCard.css)
 *   - Hat keinen Preview-Modus (keine "Testimonial fehlt"-Hinweise sichtbar)
 *
 * Briefing: _tasks/case-study-screenshots/BRIEFING.md (Component 8)
 *
 * @author Claude & Christian
 * @date   2026-05-14
 */

import React, { useState } from 'react';
import './CaseStudyCard.css';

const PLATFORM_META = {
  google: { label: 'Google', color: '#4285f4', shortCode: 'G' },
  jameda: { label: 'Jameda', color: '#006A58', shortCode: 'J' }
};

function formatRating(value) {
  if (value === null || value === undefined) return '—';
  return Number(value).toFixed(1).replace('.', ',') + ' ★';
}
function formatNumber(value) {
  if (value === null || value === undefined) return '—';
  return String(value);
}
function formatDelta(value) {
  if (value === null || value === undefined) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}`;
}
function formatDateDe(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('de-DE');
}
function getInitials(name) {
  if (!name) return '–';
  return name
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map(p => p[0]?.toUpperCase() || '').join('') || '–';
}
function capitalizeWords(str) {
  if (!str || typeof str !== 'string') return str;
  return str
    .split(/(\s+|-)/)
    .map(t => (/\s|-/.test(t) ? t : t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()))
    .join('');
}
function formatTrackingDuration(p) {
  const days = p?.daysTracked;
  const weeks = p?.weeksTracked;
  if (days === null || days === undefined) return { value: '–', unit: '' };
  if (days === 0) return { value: 'Heute', unit: '' };
  if (days === 1) return { value: '1', unit: 'Tag' };
  if (days <= 30) return { value: String(days), unit: 'Tage' };
  if (weeks === 1) return { value: '1', unit: 'Woche' };
  return { value: String(weeks || 0), unit: 'Wochen' };
}

// ---------------------------------------------------------------------------
// Inline-SVG-Chart (Dual-Axis: Sterne links, Bewertungen rechts)
// Interaktiv: Hover über einen Punkt zeigt einen Tooltip mit dem genauen Wert.
// Mobile: Tap auf einen Punkt zeigt den Tooltip ebenso (toggle).
// ---------------------------------------------------------------------------
function MiniDualChart({ chart }) {
  const { labels = [], ratings = [], reviews = [] } = chart || {};
  const [hover, setHover] = useState(null); // { series: 'rating'|'review', index: number }

  if (!labels.length) {
    return (
      <div className="csc-chart-empty">Noch keine Verlaufsdaten verfügbar</div>
    );
  }

  // Dimensions
  const W = 300, H = 140;
  const PAD_L = 26, PAD_R = 26, PAD_T = 14, PAD_B = 22;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  // Ratings-Achse: domain [3, 5]
  const rMin = 3, rMax = 5;
  // Reviews-Achse: dynamisch, ab 0, mind. 10er-Schritte
  const reviewMaxRaw = Math.max(...reviews.filter(v => v != null), 0);
  const reviewMax = Math.max(10, Math.ceil(reviewMaxRaw / 10) * 10);

  const xStep = labels.length > 1 ? innerW / (labels.length - 1) : 0;
  const xOf = i => PAD_L + i * xStep;
  const yRating = v => {
    if (v == null) return null;
    const clamped = Math.max(rMin, Math.min(rMax, v));
    return PAD_T + innerH - ((clamped - rMin) / (rMax - rMin)) * innerH;
  };
  const yReview = v => {
    if (v == null) return null;
    return PAD_T + innerH - (v / reviewMax) * innerH;
  };

  const buildPath = (vals, yFn) => {
    let d = '';
    vals.forEach((v, i) => {
      const y = yFn(v);
      if (y === null) return;
      d += (d === '' ? 'M' : 'L') + xOf(i).toFixed(1) + ',' + y.toFixed(1) + ' ';
    });
    return d.trim();
  };

  const ratingPath = buildPath(ratings, yRating);
  const reviewPath = buildPath(reviews, yReview);

  // Y-Tick-Labels (rechts: Reviews / links: Ratings)
  const ratingTicks = [3, 3.5, 4, 4.5, 5];
  const reviewTicks = [0, reviewMax / 4, reviewMax / 2, (reviewMax * 3) / 4, reviewMax].map(v => Math.round(v));

  // Tooltip-Positionierung & Inhalt
  const tooltip = (() => {
    if (!hover) return null;
    const { series, index } = hover;
    const val = series === 'rating' ? ratings[index] : reviews[index];
    if (val == null) return null;
    const y = series === 'rating' ? yRating(val) : yReview(val);
    const x = xOf(index);
    const label = series === 'rating'
      ? `${Number(val).toFixed(1).replace('.', ',')} ★`
      : `${val} Bewertungen`;
    const sublabel = labels[index] || '';
    const color = series === 'rating' ? '#2563eb' : '#f97316';
    return { x, y, label, sublabel, color, series };
  })();

  // Tooltip ist 90px breit / 36px hoch, position oben mittig über dem Punkt.
  // Bei Rand-Nähe wird er nach links/rechts verschoben, damit er nicht abgeschnitten wird.
  const tw = 96, th = 38;
  const tx = tooltip
    ? Math.max(PAD_L - 4, Math.min(W - PAD_R - tw + 4, tooltip.x - tw / 2))
    : 0;
  const ty = tooltip ? Math.max(0, tooltip.y - th - 10) : 0;
  // Pfeil/Connector vom Tooltip zum Punkt
  const arrowX = tooltip ? tooltip.x : 0;
  const arrowY1 = tooltip ? ty + th : 0;
  const arrowY2 = tooltip ? tooltip.y - 6 : 0;

  return (
    <div className="csc-chart">
      <div className="csc-chart-legend">
        <span className="csc-legend-item">
          <span className="csc-legend-dot" style={{ background: '#2563eb' }} />
          Sterne
        </span>
        <span className="csc-legend-item">
          <span className="csc-legend-dot" style={{ background: '#f97316' }} />
          Bewertungen
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="csc-chart-svg"
        role="img"
        aria-label="Verlauf Sterne und Bewertungen"
        onMouseLeave={() => setHover(null)}
      >
        {/* Gridlines (4 horizontale) */}
        {[0, 1, 2, 3, 4].map(i => {
          const y = PAD_T + (innerH * i) / 4;
          return (
            <line
              key={i}
              x1={PAD_L}
              x2={W - PAD_R}
              y1={y}
              y2={y}
              stroke="rgba(0,0,0,0.06)"
              strokeWidth="1"
            />
          );
        })}
        {/* Y-Labels links (Ratings) */}
        {ratingTicks.map((t, i) => (
          <text
            key={`rt-${i}`}
            x={PAD_L - 4}
            y={yRating(t) + 3}
            fontSize="9"
            fill="#2563eb"
            textAnchor="end"
          >
            {t}
          </text>
        ))}
        {/* Y-Labels rechts (Reviews) */}
        {reviewTicks.map((t, i) => {
          const yT = PAD_T + innerH - (t / reviewMax) * innerH;
          return (
            <text
              key={`rv-${i}`}
              x={W - PAD_R + 4}
              y={yT + 3}
              fontSize="9"
              fill="#f97316"
              textAnchor="start"
            >
              {t}
            </text>
          );
        })}
        {/* X-Labels */}
        {labels.map((l, i) => (
          <text
            key={`xl-${i}`}
            x={xOf(i)}
            y={H - 6}
            fontSize="9"
            fill="rgba(0,0,0,0.5)"
            textAnchor="middle"
          >
            {l}
          </text>
        ))}
        {/* Lines (mit Reveal-Animation beim ersten Render via CSS) */}
        {reviewPath && (
          <path
            d={reviewPath}
            fill="none"
            stroke="#f97316"
            strokeWidth="2"
            className="csc-chart-line csc-chart-line-review"
          />
        )}
        {ratingPath && (
          <path
            d={ratingPath}
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            className="csc-chart-line csc-chart-line-rating"
          />
        )}
        {/* Sichtbare Punkte */}
        {ratings.map((v, i) => {
          const y = yRating(v);
          if (y === null) return null;
          const isActive = hover?.series === 'rating' && hover.index === i;
          return (
            <g key={`rp-${i}`} className="csc-chart-point-group">
              {/* Weißer Ring (nur sichtbar bei aktiv) */}
              {isActive && (
                <circle cx={xOf(i)} cy={y} r="7" fill="#fff" stroke="#2563eb" strokeWidth="2" />
              )}
              <circle
                cx={xOf(i)}
                cy={y}
                r={isActive ? 4 : 3}
                fill="#2563eb"
                className="csc-chart-point"
              />
            </g>
          );
        })}
        {reviews.map((v, i) => {
          const y = yReview(v);
          if (y === null) return null;
          const isActive = hover?.series === 'review' && hover.index === i;
          return (
            <g key={`vp-${i}`} className="csc-chart-point-group">
              {isActive && (
                <circle cx={xOf(i)} cy={y} r="7" fill="#fff" stroke="#f97316" strokeWidth="2" />
              )}
              <circle
                cx={xOf(i)}
                cy={y}
                r={isActive ? 4 : 3}
                fill="#f97316"
                className="csc-chart-point"
              />
            </g>
          );
        })}

        {/* Unsichtbare Hover-Hotspots: größere Hitboxen für leichteres Treffen.
            Über den sichtbaren Punkten platziert (deshalb am Ende des SVG). */}
        {ratings.map((v, i) => {
          const y = yRating(v);
          if (y === null) return null;
          return (
            <circle
              key={`rh-${i}`}
              cx={xOf(i)}
              cy={y}
              r="12"
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHover({ series: 'rating', index: i })}
              onClick={() => setHover(prev =>
                prev?.series === 'rating' && prev.index === i ? null : { series: 'rating', index: i }
              )}
            />
          );
        })}
        {reviews.map((v, i) => {
          const y = yReview(v);
          if (y === null) return null;
          return (
            <circle
              key={`vh-${i}`}
              cx={xOf(i)}
              cy={y}
              r="12"
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHover({ series: 'review', index: i })}
              onClick={() => setHover(prev =>
                prev?.series === 'review' && prev.index === i ? null : { series: 'review', index: i }
              )}
            />
          );
        })}

        {/* Tooltip */}
        {tooltip && (
          <g className="csc-chart-tooltip" pointerEvents="none">
            {/* Connector */}
            <line
              x1={arrowX}
              x2={arrowX}
              y1={arrowY1}
              y2={arrowY2}
              stroke={tooltip.color}
              strokeWidth="1.5"
              strokeDasharray="2 2"
            />
            {/* Tooltip-Bubble */}
            <rect
              x={tx}
              y={ty}
              width={tw}
              height={th}
              rx="6"
              fill={tooltip.color}
            />
            <text
              x={tx + tw / 2}
              y={ty + 15}
              fontSize="11"
              fontWeight="700"
              fill="#fff"
              textAnchor="middle"
            >
              {tooltip.label}
            </text>
            <text
              x={tx + tw / 2}
              y={ty + 29}
              fontSize="9"
              fill="rgba(255,255,255,0.85)"
              textAnchor="middle"
            >
              {tooltip.sublabel}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function CaseStudyCard({ data }) {
  const platforms = data?.platforms || {};
  const availablePlatforms = Object.entries(platforms)
    .filter(([, p]) => p && p.available)
    .map(([k]) => k);

  const [activeTab, setActiveTab] = useState(availablePlatforms[0] || 'google');
  const [modalPayload, setModalPayload] = useState(null);

  if (!data) return null;

  // Pause-Modus
  if (data.paused) {
    const customer = data.customer || {};
    return (
      <div className="csc-card csc-paused">
        <div className="csc-paused-title">Case-Study pausiert</div>
        {customer.companyName && <div className="csc-paused-sub">{customer.companyName}</div>}
        <div className="csc-paused-text">
          Wir warten auf erste messbare Aktivität (Lieferung der Drucksachen oder QR-Code-Scans).
        </div>
      </div>
    );
  }

  if (availablePlatforms.length === 0) return null;

  const activeData = platforms[activeTab];
  const customer = data.customer || {};
  const testimonial = data.testimonial || {};
  const specialty = capitalizeWords(customer.specialty || '');
  const city = capitalizeWords(customer.city || '');
  const hasQuote = Boolean(testimonial?.quote?.trim());
  const testimonialEnabled = testimonial?.enabled !== false;

  return (
    <>
      <div className="csc-card">
        {/* Header */}
        <div className="csc-card-header">
          <div>
            <div className="csc-card-name">{customer.companyName}</div>
            <div className="csc-card-meta">
              {specialty && <span>{specialty}</span>}
              {specialty && city && <span className="csc-card-meta-dot" />}
              {city && <span>{city}</span>}
            </div>
          </div>
          <div className="csc-card-badges">
            {specialty && <span className="csc-card-tag">{specialty}</span>}
            <span className="csc-live-badge">
              <span className="csc-live-dot" />
              Live
            </span>
          </div>
        </div>

        {/* Plattform-Tabs */}
        <div className="csc-tabs">
          {['google', 'jameda'].map(p => {
            const meta = PLATFORM_META[p];
            const isAvailable = availablePlatforms.includes(p);
            const isActive = activeTab === p;
            if (!isAvailable) return null; // Verstecke Tabs ohne Daten komplett auf Public-Site
            return (
              <button
                key={p}
                type="button"
                onClick={() => setActiveTab(p)}
                className={`csc-tab ${isActive ? 'csc-tab-active' : ''}`}
              >
                <span className="csc-tab-icon" style={{ background: meta.color }}>
                  {meta.shortCode}
                </span>
                {meta.label}
              </button>
            );
          })}
        </div>

        {/* Tab-Content */}
        <div className="csc-content">
          {activeData && (activeData.daysTracked > 0 || activeData.weeksTracked > 0) ? (
            <>
              <MetricsBlock platformData={activeData} />
              <MiniDualChart chart={activeData.chart} />
            </>
          ) : (
            <div className="csc-no-history">
              Verlaufsdaten werden in Kürze verfügbar.
            </div>
          )}

          {activeData?.screenshot?.url && (
            <button
              type="button"
              onClick={() => setModalPayload({ ...activeData.screenshot, platform: activeTab, dateLabel: formatDateDe(activeData.screenshot.captureDate) })}
              className="csc-screenshot-link"
            >
              📷 {PLATFORM_META[activeTab].label}-Profil zum Start am {formatDateDe(activeData.screenshot.captureDate)} ansehen →
            </button>
          )}

          {/* Testimonial */}
          {testimonialEnabled && hasQuote ? (
            <div className="csc-testimonial">
              {testimonial?.avatarUrl ? (
                <img src={testimonial.avatarUrl} alt={testimonial.author || ''} className="csc-avatar" />
              ) : (
                <div className="csc-avatar csc-avatar-initials">
                  {getInitials(testimonial?.author)}
                </div>
              )}
              <div className="csc-testimonial-body">
                <div className="csc-testimonial-quote">„{testimonial.quote}"</div>
                {testimonial?.author && <div className="csc-testimonial-author">{testimonial.author}</div>}
                {testimonial?.role && <div className="csc-testimonial-role">{testimonial.role}</div>}
              </div>
            </div>
          ) : (
            // Platzhalter für gleiche Card-Höhe in Side-by-Side-Layouts
            <div className="csc-testimonial-placeholder" aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Screenshot-Modal */}
      {modalPayload && (
        <div className="csc-modal-backdrop" onClick={() => setModalPayload(null)}>
          <div className="csc-modal" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setModalPayload(null)}
              className="csc-modal-close"
              aria-label="Schließen"
            >
              ×
            </button>
            <h3 className="csc-modal-title">
              {PLATFORM_META[modalPayload.platform]?.label || ''}-Profil zum Start
            </h3>
            <div className="csc-modal-imgwrap">
              <img src={modalPayload.url} alt="" className="csc-modal-img" />
            </div>
            <div className="csc-modal-caption">
              Stand: {modalPayload.dateLabel} — vor Beginn der Zusammenarbeit mit smartRATINGS
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
function MetricsBlock({ platformData }) {
  const {
    startRating,
    startReviewCount,
    currentRating,
    currentReviewCount,
    deltaReviews
  } = platformData;
  const duration = formatTrackingDuration(platformData);

  return (
    <div className="csc-metrics">
      <div className="csc-metric-stack">
        <div className="csc-metric-half csc-metric-red">
          <div className="csc-metric-value">{formatRating(startRating)}</div>
          <div className="csc-metric-label">Start</div>
        </div>
        <div className="csc-metric-half csc-metric-green">
          <div className="csc-metric-value">{formatRating(currentRating)}</div>
          <div className="csc-metric-label">aktuell</div>
        </div>
      </div>
      <div className="csc-metric-stack">
        <div className="csc-metric-half csc-metric-red">
          <div className="csc-metric-value">{formatNumber(startReviewCount)}</div>
          <div className="csc-metric-label">Start</div>
        </div>
        <div className="csc-metric-half csc-metric-green">
          <div className="csc-metric-value">{formatNumber(currentReviewCount)}</div>
          <div className="csc-metric-label">Bewertungen</div>
        </div>
      </div>
      <div className="csc-metric-stack csc-metric-blue-stack">
        <div className="csc-metric-half csc-metric-blue">
          <div className="csc-metric-value">{formatDelta(deltaReviews)}</div>
          <div className="csc-metric-label">Bewertungen</div>
        </div>
        <div className="csc-metric-half csc-metric-blue-light">
          <div className="csc-metric-value">{duration.value}</div>
          <div className="csc-metric-label">{duration.unit}</div>
        </div>
      </div>
    </div>
  );
}
